import { useState, useEffect } from 'react';
import { Opportunity } from '@/types/opportunity';
import { opportunityStorage } from '@services/storage/opportunity-storage';
import { logger } from '@utils/logger';

export function useOpportunity(sessionId?: string) {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sessionId) {
      loadOpportunities(sessionId);
    }
  }, [sessionId]);

  const loadOpportunities = async (id: string) => {
    try {
      setLoading(true);
      const data = await opportunityStorage.getBySessionId(id);
      setOpportunities(data);
      setError(null);
    } catch (err) {
      logger.error('Failed to load opportunities:', err);
      setError('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  };

  const createOpportunity = async (
    opportunity: Omit<Opportunity, 'opportunityId' | 'timestamp'>
  ): Promise<Opportunity> => {
    try {
      const created = await opportunityStorage.create(opportunity);
      setOpportunities((prev) => [...prev, created]);
      return created;
    } catch (err) {
      logger.error('Failed to create opportunity:', err);
      throw err;
    }
  };

  const updateOpportunity = async (
    id: string,
    updates: Partial<Opportunity>
  ): Promise<void> => {
    try {
      await opportunityStorage.update(id, updates);
      setOpportunities((prev) =>
        prev.map((opp) => (opp.opportunityId === id ? { ...opp, ...updates } : opp))
      );
    } catch (err) {
      logger.error('Failed to update opportunity:', err);
      throw err;
    }
  };

  const deleteOpportunity = async (id: string): Promise<void> => {
    try {
      await opportunityStorage.delete(id);
      setOpportunities((prev) =>
        prev.filter((opp) => opp.opportunityId !== id)
      );
    } catch (err) {
      logger.error('Failed to delete opportunity:', err);
      throw err;
    }
  };

  return {
    opportunities,
    loading,
    error,
    createOpportunity,
    updateOpportunity,
    deleteOpportunity,
    reload: sessionId ? () => loadOpportunities(sessionId) : undefined,
  };
}
